import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import {
    VIDEO_URL_ERROR,
    getVideoLinkKey,
    getVideoProviderLabel,
    getVideoThumbnailUrl,
    getYouTubeThumbnailUrl,
    parseVideoUrl,
} from '../utils/video';
import './VideoLinkField.css';

const DEFAULT_HINT = 'YouTube (watch, youtu.be, shorts or live links) or Rumble (rumble.com/v…-title.html page links or rumble.com/embed/… links). The video plays at the top of the post with your commentary below it, and the original channel is credited and linked. Leave the thumbnail empty to use the video\'s own; an uploaded thumbnail replaces it.';

// Shown under the preview: where the credit link will point.
const describeSource = (preview) => {
    if (!preview) {
        return '';
    }
    if (preview.url) {
        return preview.url.replace(/^https:\/\/(www\.)?/, '');
    }
    return preview.provider === 'youtube' ? `youtube.com/watch?v=${preview.id}` : '';
};

// Offline stand-in when the platform can't be asked: YouTube ids and Rumble
// embed ids are known from the link itself.
const buildFallbackPreview = (parsed, key) => {
    if (parsed.provider === 'youtube') {
        return {
            key,
            provider: 'youtube',
            id: parsed.id,
            url: parsed.url,
            title: '',
            channel: '',
            thumbnail: getYouTubeThumbnailUrl(parsed.id),
        };
    }
    if (parsed.embedId) {
        return { key, provider: 'rumble', id: parsed.embedId, url: '', title: '', channel: '', thumbnail: '' };
    }
    return null;
};

// The edit form's stored video as a ready preview, keyed like a parsed link,
// so opening the form doesn't look the video up again.
const buildStoredPreview = (video) => {
    if (!video || !video.id || typeof video.url !== 'string') {
        return null;
    }
    const key = getVideoLinkKey(parseVideoUrl(video.url));
    if (!key) {
        return null;
    }
    return {
        key,
        provider: video.provider === 'rumble' ? 'rumble' : 'youtube',
        id: video.id,
        url: video.url,
        title: typeof video.title === 'string' ? video.title : '',
        channel: typeof video.channel === 'string' ? video.channel : '',
        thumbnail: getVideoThumbnailUrl(video),
    };
};

// Author-portal field for a Condoned Media video link. Validates the URL as
// the writer types, then asks the backend for the video's title, channel and
// thumbnail (debounced, once per video) and shows a preview card.
// `onPreview` receives `{ key, provider, id, title, channel, thumbnail }` so
// the form can prefill the document title. `storedVideo` (edit form) is the
// post's saved video: its link previews from that data without a lookup.
function VideoLinkField({
    value,
    onChange,
    onPreview,
    storedVideo = null,
    disabled = false,
    required = false,
    label = 'Video link (YouTube or Rumble) *',
    hint = DEFAULT_HINT,
}) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    const parsed = useMemo(() => parseVideoUrl(trimmed), [trimmed]);
    const linkKey = getVideoLinkKey(parsed);
    const storedPreview = useMemo(() => buildStoredPreview(storedVideo), [storedVideo]);
    const startsStored = Boolean(storedPreview && storedPreview.key === linkKey);

    const [preview, setPreview] = useState(startsStored ? storedPreview : null);
    const [status, setStatus] = useState(startsStored ? 'ready' : 'idle');
    const [message, setMessage] = useState('');
    // Bumped by "Try again" after a failed lookup.
    const [attempt, setAttempt] = useState(0);
    // Last definitive answer `{ key, preview, status, message }`. Network
    // failures aren't kept, so the same link can be looked up again.
    const lastResultRef = useRef(null);
    const onPreviewRef = useRef(onPreview);

    useEffect(() => {
        onPreviewRef.current = onPreview;
    }, [onPreview]);

    useEffect(() => {
        if (!trimmed) {
            setPreview(null);
            setStatus('idle');
            setMessage('');
            return undefined;
        }

        if (!parsed) {
            setPreview(null);
            setStatus('invalid');
            setMessage(VIDEO_URL_ERROR);
            return undefined;
        }

        // Back on a video already answered for (or the saved one): show that
        // again — also when a link is edited and reverted before its lookup
        // ran, which would otherwise leave "Fetching…" on screen.
        const known = lastResultRef.current && lastResultRef.current.key === linkKey
            ? lastResultRef.current
            : (storedPreview && storedPreview.key === linkKey
                ? { preview: storedPreview, status: 'ready', message: '' }
                : null);
        if (known) {
            setPreview(known.preview);
            setStatus(known.status);
            setMessage(known.message);
            return undefined;
        }

        let active = true;
        setStatus('loading');
        setMessage('Fetching video details…');

        const timer = setTimeout(async () => {
            try {
                const response = await axios.get(`${API_URL}/api/posts/video-preview`, {
                    params: { url: trimmed },
                });
                if (!active) {
                    return;
                }
                const data = response.data || {};
                const next = {
                    key: linkKey,
                    provider: data.provider === 'rumble' ? 'rumble' : 'youtube',
                    id: typeof data.id === 'string' ? data.id : '',
                    url: typeof data.url === 'string' ? data.url : '',
                    title: typeof data.title === 'string' ? data.title : '',
                    channel: typeof data.channel === 'string' ? data.channel : '',
                    thumbnail: typeof data.thumbnail === 'string' ? data.thumbnail : '',
                };
                lastResultRef.current = { key: linkKey, preview: next, status: 'ready', message: '' };
                setPreview(next);
                setStatus('ready');
                setMessage('');
                onPreviewRef.current?.(next);
            } catch (error) {
                if (!active) {
                    return;
                }
                const httpStatus = error.response?.status;
                const serverMessage = error.response?.data?.message;

                if (httpStatus === 400 || httpStatus === 404) {
                    const notFound = serverMessage || 'That video could not be found';
                    lastResultRef.current = { key: linkKey, preview: null, status: 'error', message: notFound };
                    setPreview(null);
                    setStatus('error');
                    setMessage(notFound);
                    return;
                }

                // Rate limit, platform or network trouble: transient, so
                // not remembered (the writer can try again).
                const fallback = buildFallbackPreview(parsed, linkKey);
                setPreview(fallback);
                setStatus('unavailable');
                if (httpStatus === 429) {
                    setMessage(serverMessage || 'Too many lookups right now — wait a few minutes and try again.');
                } else {
                    setMessage(fallback
                        ? 'Could not fetch the video details right now — the link is valid and will still be saved.'
                        : 'Could not reach Rumble to look up this page link right now — try again in a moment, or paste the video\'s embed link (Share → Embed on Rumble).');
                }
                if (fallback) {
                    onPreviewRef.current?.(fallback);
                }
            }
        }, 500);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [trimmed, parsed, linkKey, storedPreview, attempt]);

    const providerLabel = getVideoProviderLabel(preview?.provider || parsed?.provider);
    const inputId = useId();
    const hintId = `${inputId}-hint`;
    const statusId = `${inputId}-status`;
    const describedBy = [hint && hintId, message && statusId].filter(Boolean).join(' ');

    return (
        <div className="video-link-field">
            <label className="video-link-field__label" htmlFor={inputId}>{label}</label>
            {/* type="text": the browser's url validation would block the
                links without https:// and bare ids this field accepts. */}
            <input
                id={inputId}
                aria-describedby={describedBy || undefined}
                type="text"
                name="videoUrl"
                value={value || ''}
                onChange={(event) => onChange(event.target.value)}
                placeholder="https://www.youtube.com/watch?v=…  or  https://rumble.com/v…-title.html"
                disabled={disabled}
                required={required}
                autoComplete="off"
                spellCheck={false}
                inputMode="url"
            />
            {hint && <small id={hintId} className="video-link-field__hint">{hint}</small>}
            {message && (
                <small
                    id={statusId}
                    className={`video-link-field__status video-link-field__status--${status}`}
                    role="status"
                >
                    {message}
                    {status === 'unavailable' && (
                        <button
                            type="button"
                            className="video-link-field__retry"
                            onClick={() => setAttempt((count) => count + 1)}
                            disabled={disabled}
                        >
                            Try again
                        </button>
                    )}
                </small>
            )}
            {preview && (
                <div className="video-link-field__preview" aria-live="polite">
                    <span className="video-link-field__preview-frame">
                        {preview.thumbnail && <img src={preview.thumbnail} alt="" loading="lazy" />}
                        <span className="video-link-field__preview-badge" aria-hidden="true">▶</span>
                    </span>
                    <div className="video-link-field__preview-text">
                        <strong>{preview.title || `${providerLabel} video`}</strong>
                        {preview.channel && <span>{preview.channel}</span>}
                        <span className="video-link-field__preview-source">
                            {providerLabel}{describeSource(preview) ? ` · ${describeSource(preview)}` : ''}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default VideoLinkField;
